import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Label, LABEL_TYPE } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { DetailsPageLayout } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { EditPenIcon, PlayIcon, TrashIcon } from '@/resources';
import {
  useDeletePlayerReleaseMutation,
  useGetPlayerReleaseByIdQuery,
  usePublishPlayerRelease
} from '@/services/playerRelease.service';

export const PlayerReleaseDetails = () => {
  const [t] = useTranslation('common');
  const { playerReleaseId } = useParams();
  const navigate = useNavigate();

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [error, setError] = useState<number>();

  const {
    data: playerRelease,
    isLoading: isLoadingRelease,
    refetch
  } = useGetPlayerReleaseByIdQuery(Number(playerReleaseId), {
    onSuccess: () => undefined,
    onError: (error) => {
      setError(error.status);
    }
  });

  const handleEditPlayerRelease = () => {
    navigate(`/player-releases/${playerReleaseId}/edit`);
  };

  const handleDelete = () => {
    setIsOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const handlePublish = () => {
    publishPlayerRelease(Number(playerReleaseId));
  };

  const { mutate: deletePlayerRelease } = useDeletePlayerReleaseMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      navigate('/player-releases');
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const { mutate: publishPlayerRelease } = usePublishPlayerRelease(queryClient, {
    onSuccess: () => {
      navigate(`${PRIVATE_PAGE_ROUTES.PLAYER_RELEASES}/${playerReleaseId}`);
      showToast(ToastType.SUCCESS, t('playerReleaseUpdated'));
      refetch();
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages?.[0].DisplayMessage);
    }
  });

  const headerButtonsConfig: ButtonConfig[] = [
    {
      label: t('publish'),
      variant: ButtonVariant.PRIMARY,
      type: BUTTON_TYPE.BUTTON,
      handleOnClick: handlePublish,
      isDisabled: playerRelease?.data.isPublic,
      className: 'hidden md:block'
    },
    {
      icon: <EditPenIcon className='text-primary-blue' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleEditPlayerRelease,
      permissions: [PERMISSION_TYPE.UPDATE_PLAYER_RELEASE]
    },
    {
      icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleDelete,
      permissions: [PERMISSION_TYPE.DELETE_PLAYER_RELEASE]
    }
  ];

  return error ? (
    <ErrorPage code={error} />
  ) : (
    <div className='overflow-auto'>
      <DetailsPageLayout
        icon={<PlayIcon className='stroke-[0.5] text-lg text-primary-blue' />}
        iconPath={'/player-releases'}
        path={[{ label: t('playerReleaseDetails') }]}
        title={`${playerRelease?.data.version}`}
        tag={playerRelease?.data?.isPublic ? t('published') : t('drafted')}
        isLoading={isLoadingRelease}
        headerButtons={headerButtonsConfig}>
        <div className='mt-3 flex space-x-9 md:mt-12'>
          <div className='flex w-full flex-col gap-4'>
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('version')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={playerRelease?.data?.version}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('url')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={playerRelease?.data?.url}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
          </div>
        </div>
      </DetailsPageLayout>
      {isOpenDeleteModal && (
        <ConfirmationModal
          isOpen={isOpenDeleteModal}
          message={t('deleteMessage', { item: playerRelease?.data.version })}
          onClose={onCloseDeleteModal}
          onConfirm={() => deletePlayerRelease(playerRelease?.data.id)}
        />
      )}
    </div>
  );
};
